import React from "react";
import SafeNumberInput from "../SafeNumberInput";
import { api } from "../utils/api";
import { Link } from "react-router-dom";
import AudioPlayer from "react-h5-audio-player";
import { useReadAloud, ReadableItem } from "../utils/useReadAloud";
import { ReadAloudBar } from "../utils/ReadAloudBar";
import { HighlightedText, Mark } from "../utils/HighlightedText";
import { ArticleViewer } from "../ArticleViewer";

export interface INewsItem {
  url: string;
  source: string;
  published_at: number;
  title: string;
  title_marks: Mark[];
  tickers: string;
  ticker_changes: Record<string, number>; // US ticker -> regular/premarket change_rel (whichever session applies)
  read: number; // 0, 1, 2 (0 = not read, 1 = read, 2 = bookmarked)
  audio_url: string;
}

interface INewsRequest {
  query: string;
  content_query: string;
  start_date: string;
  end_date: string;
  source: string;
  page: number;
  table: string;
  saved_only: boolean;
  reverse: boolean;
  us_tickers_only: boolean;
  tickers_query: string;
}

interface IPlayerData {
  playerUrl: string;
  currentFile: INewsItem;
}

export const MobileNewsSearch = (props: {}) => {
  const [news, setNews] = React.useState<INewsItem[]>([]);
  const [newsRequest, setNewsRequest] = React.useState<INewsRequest>({
    query: "",
    content_query: "",
    start_date: "",
    end_date: "",
    source: "bloomberg",
    page: 1,
    table: "articles",
    saved_only: false,
    reverse: false,
    us_tickers_only: false,
    tickers_query: "",
  });

  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");

  const readAloud = useReadAloud([news]);

  const readableItems: ReadableItem[] = news
    .filter((item) => item.read === 0)
    .map((item) => ({ key: item.url, texts: [item.title] }));

  const onItemDone = React.useCallback((key: string) => {
    markArticle(key, 1);
  }, [news]);
  const [playerData, setPlayerData] = React.useState<IPlayerData | null>(null);

  const playerRef = React.useRef<any>(null);
  const [playbackSpeed, setPlaybackSpeed] = React.useState(1);

  React.useEffect(() => {
    if (playerRef.current?.audio?.current && playbackSpeed !== 1) {
      playerRef.current.audio.current.playbackRate = playbackSpeed;
    }
  }, [playerData, playbackSpeed]);


  // fetch bloomberg on load, only if there is no data already (prevent reload on back button on mobile)
  React.useEffect(() => {
    if (news.length === 0) getNews();
  }, []);

  const getNews = async () => {
    setErrorMessage("");

    let startDate = newsRequest.start_date;
    let endDate = newsRequest.end_date;

    if (newsRequest.content_query.trim()) {
      if (!startDate) {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        startDate = d.toISOString().split("T")[0];
      }
      if (!endDate) {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        endDate = d.toISOString().split("T")[0];
      }
      const diffDays =
        (new Date(endDate).getTime() - new Date(startDate).getTime()) /
        86_400_000;
      if (diffDays > 40) {
        setErrorMessage("Content Q: date range cannot exceed 40 days.");
        return;
      }
      if (startDate !== newsRequest.start_date || endDate !== newsRequest.end_date) {
        setNewsRequest((prev) => ({ ...prev, start_date: startDate, end_date: endDate }));
      }
    }

    setIsLoading(true);

    try {
      const req = { ...newsRequest, start_date: startDate, end_date: endDate };
      const response = await api.post<INewsItem[]>("/search", req);
      setNews(response.data);
    } catch (error: any) {
      console.error("Error fetching news:", error);

      const message =
        error?.response?.data?.detail || error.message || "Error fetching news";
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const markArticle = (url: string, read: number) => {
    const newMark = read % 3; // 0 = not read, 1 = read, 2 = bookmarked

    // capture the previous read value so we can revert on error
    const prevRead = news.find((item) => item.url === url)?.read ?? read;

    setNews((prevNews) =>
      prevNews.map((item) =>
        item.url === url ? { ...item, read: newMark } : item,
      ),
    );

    api
      .post("/mark", { url, read: newMark, table: "articles" })
      .then((response) => {
        const data = response.data;
        if (!data?.success) {
          console.error("data.success is false");
          // revert the change
          setNews((prevNews) =>
            prevNews.map((item) =>
              item.url === url ? { ...item, read: prevRead } : item,
            ),
          );
          throw new Error("Failed to mark news as read");
        }
      })
      .catch((error) => {
        // revert the change
        setNews((prevNews) =>
          prevNews.map((item) =>
            item.url === url ? { ...item, read: prevRead } : item,
          ),
        );
        console.error("Error marking news as read:", error);
      });
  };

  function unixTimestampToNewYork(timestamp: number): [string, Date] {
    const date = new Date(timestamp * 1000);
    // convert to New York time, format as 17 Apr 2025 HH:MM
    const newyorkDate: Date = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" }),
    );

    const day = String(newyorkDate.getDate()).padStart(2, "0");
    const month = String(newyorkDate.getMonth() + 1).padStart(2, "0");
    const year = newyorkDate.getFullYear();
    const hours = String(newyorkDate.getHours()).padStart(2, "0");
    const minutes = String(newyorkDate.getMinutes()).padStart(2, "0");
    const seconds = String(newyorkDate.getSeconds()).padStart(2, "0");

    // get the day of the week
    const options: Intl.DateTimeFormatOptions = { weekday: "short" };
    const dayOfWeek = newyorkDate.toLocaleDateString("en-US", options);

    return [
      `${year}-${month}-${day} ${hours}:${minutes} (${dayOfWeek})`,
      newyorkDate,
    ];
  }

  function playNext() {
    // mark current article as read (1) before playing next
    if (playerData?.currentFile) {
      markArticle(playerData.currentFile.url, 1);
    }

    // play the next audio file in the list, make sure the file has link
    const currentIndex = news.findIndex(
      (file) => file.audio_url === playerData?.playerUrl,
    );

    // go forward until we find a file with link
    let nextIndex = currentIndex + 1;

    while (nextIndex < news.length && news[nextIndex].audio_url === "") {
      nextIndex++;
    }

    if (nextIndex < news.length) {
      playAudio(news[nextIndex]);
    } else {
      setPlayerData(null);
    }
  }

  function playPrevious() {
    // play the previous audio file in the list, make sure the file has link
    const currentIndex = news.findIndex(
      (file) => file.audio_url === playerData?.playerUrl,
    );

    // go back until we find a file with link
    let previousIndex = currentIndex - 1;

    while (previousIndex >= 0 && news[previousIndex].audio_url === "") {
      previousIndex--;
    }

    if (previousIndex >= 0) {
      playAudio(news[previousIndex]);
    } else {
      setPlayerData(null);
    }
  }

  function playAudio(file: INewsItem) {
    // extract the id from the url
    setPlayerData({ playerUrl: file.audio_url, currentFile: file });
  }

  function isPlaying(file: INewsItem) {
    return playerData?.currentFile.audio_url === file.audio_url;
  }

  function updateProgress(url: string, progress: number) {
    // TODO: mark article as read when the audio is finished
  }

  return (
    <div className="w-full h-full flex flex-col relative">
      {/* Scrollable: search controls + articles list */}
      <div className="flex-1 overflow-auto p-2">
        <Link to="/mobile" className="btn btn-default flex-grow">
          To Menu
        </Link>

        <form
          className="flex flex-col mb-8 gap-0"
          onSubmit={(e) => {
            e.preventDefault();
            getNews();
          }}
        >
          <div className="form-group">
            <label htmlFor="page">Page:</label>
            <SafeNumberInput
              id="page"
              value={newsRequest.page}
              onChange={(value) =>
                setNewsRequest({ ...newsRequest, page: value })
              }
              min={1}
              max={10000}
            />
          </div>

          <div className="form-group">
            <label htmlFor="start_date">Start Date:</label>
            <input
              type="text"
              id="start_date"
              placeholder="YYYY-MM-DD"
              value={newsRequest.start_date}
              onChange={(e) =>
                setNewsRequest({ ...newsRequest, start_date: e.target.value })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date">End Date:</label>
            <input
              type="text"
              id="end_date"
              placeholder="YYYY-MM-DD"
              value={newsRequest.end_date}
              onChange={(e) =>
                setNewsRequest({ ...newsRequest, end_date: e.target.value })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="source">Source:</label>
            <select
              id="source"
              value={newsRequest.source}
              onChange={(e) => {
                setNewsRequest({ ...newsRequest, source: e.target.value });
              }}
              className="border border-gray-300 rounded p-1 block w-full"
            >
              <option value="">All</option>

              <optgroup label="Main">
                <option value="bloomberg">Bloomberg</option>
                <option value="reuters">Reuters</option>
                <option value="wsj">WSJ</option>
                <option value="barrons">Barron's</option>
                <option value="inv_news">Inv News</option>
                <option value="cnbc">CNBC</option>
              </optgroup>

              <optgroup label="Political">
                <option value="politico">Politico</option>
                <option value="the_hill">The Hill</option>
                <option value="axios">Axios</option>
              </optgroup>

              <optgroup label="Papers">
                <option value="ft">Financial Times</option>
                <option value="la_times">Los Angeles Times</option>
                <option value="ny_times">NY Times</option>
                <option value="ny_post">NY Post</option>
                <option value="washington_post">Washington Post</option>
              </optgroup>

              <optgroup label="Wires">
                <option value="ap">AP</option>
                <option value="marketwatch">MarketWatch</option>
              </optgroup>

              <optgroup label="TV">
                <option value="fox">Fox News</option>
                <option value="nbc">NBC</option>
                <option value="cbs">CBS</option>
                <option value="cnn">CNN</option>
                <option value="abc">ABC</option>
              </optgroup>

              <optgroup label="Crypto">
                <option value="coindesk">CoinDesk</option>
                <option value="cointelegraph">CoinTelegraph</option>
                <option value="decrypt">Decrypt</option>
              </optgroup>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="keywords">Title Q:</label>
            <input
              type="text"
              id="keywords"
              value={newsRequest.query}
              onChange={(e) =>
                setNewsRequest({ ...newsRequest, query: e.target.value })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="saved_only">
              <input
                type="checkbox"
                id="saved_only"
                checked={newsRequest.saved_only}
                onChange={(e) =>
                  setNewsRequest({
                    ...newsRequest,
                    saved_only: e.target.checked,
                  })
                }
              />
              &nbsp; Saved only
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="us_tickers_only">
              <input
                type="checkbox"
                id="us_tickers_only"
                checked={newsRequest.us_tickers_only}
                onChange={(e) =>
                  setNewsRequest({
                    ...newsRequest,
                    us_tickers_only: e.target.checked,
                  })
                }
              />
              &nbsp; US Tickers only
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="tickers_query">Tickers Query:</label>
            <input
              type="text"
              id="tickers_query"
              value={newsRequest.tickers_query}
              onChange={(e) =>
                setNewsRequest({
                  ...newsRequest,
                  tickers_query: e.target.value,
                })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="content_query">Content Q:</label>
            <input
              type="text"
              id="content_query"
              value={newsRequest.content_query}
              onChange={(e) =>
                setNewsRequest({ ...newsRequest, content_query: e.target.value })
              }
            />
          </div>

          <div className="form-group w-full flex justify-between gap-3">
            <button type="submit" className="btn btn-default grow">
              Search
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setNews((prev) => prev.map((item) => ({ ...item, read: 0 })))}
            >
              Unread all
            </button>
          </div>
        </form>

        <ArticlesTable
          news={news}
          errorMessage={errorMessage}
          isLoading={isLoading}
          markArticle={markArticle}
          unixTimestampToNewYork={unixTimestampToNewYork}
          setActiveArticleUrl={setActiveArticleUrl}
          readingArticleUrl={readAloud.currentlyReadingKey}
        />
      </div>

      {activeArticleUrl !== "" && (
        <ArticleViewer
          url={activeArticleUrl}
          setActiveArticleUrl={setActiveArticleUrl}
          contentQuery={newsRequest.content_query}
        />
      )}

      {/* Audio controls pinned to bottom */}
      <div className="shrink-0">
        {activeArticleUrl === "" && (
          <ReadAloudBar
            voiceControls={readAloud.voiceControls}
            setVoiceControls={readAloud.setVoiceControls}
            toggleReading={readAloud.toggleReading}
            skipItem={readAloud.skipItem}
            items={readableItems}
            onItemDone={onItemDone}
            idPrefix="mobile-news-"
          />
        )}
        <div
          className={`${playerData ? "block" : "hidden"} border-t border-gray-300`}
        >
          {playerData && (
            <AudioPlayer
              ref={playerRef}
              header={<div>{playerData.currentFile.title}</div>}
              footer={
                <div className="flex justify-between">
                  <select
                    title="Speed:"
                    value={playbackSpeed}
                    onChange={(e) => {
                      const speed = parseFloat(e.target.value);
                      setPlaybackSpeed(speed);
                      if (playerRef.current?.audio?.current) {
                        playerRef.current.audio.current.playbackRate = speed;
                      }
                    }}
                  >
                    <option value="1">1x</option>
                    <option value="1.25">1.25x</option>
                    <option value="1.5">1.5x</option>
                    <option value="1.75">1.75x</option>
                    <option value="2">2x</option>
                    <option value="2.25">2.25x</option>
                    <option value="2.5">2.5x</option>
                  </select>
                  <p className="text-gray-500">
                    {playerData!.currentFile.source}{" "}
                    {
                      unixTimestampToNewYork(
                        playerData!.currentFile.published_at,
                      )[0]
                    }
                  </p>
                </div>
              }
              key={playerData?.playerUrl}
              autoPlay
              src={playerData?.playerUrl}
              onEnded={playNext}
              onClickNext={playNext}
              onClickPrevious={playPrevious}
              showSkipControls={true}
              volume={1}
              loop={false}
              customVolumeControls={[]}
              customAdditionalControls={[]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ArticlesTable = (props: {
  news: INewsItem[];
  errorMessage: string;
  isLoading: boolean;
  markArticle: (url: string, mark: number) => void;
  unixTimestampToNewYork: (timestamp: number) => [string, Date];
  setActiveArticleUrl: (url: string) => void;
  readingArticleUrl: string;
}) => {
  if (props.isLoading) {
    return <div className="text-center">Loading...</div>;
  }

  if (props.errorMessage !== "") {
    return <p className="text-center">{props.errorMessage}</p>;
  }

  if (props.news.length === 0) {
    return <p className="text-center">No news found</p>;
  }

  function addMarkClass(mark: number): string {
    switch (mark) {
      case 0:
        return "bg-gray-400";
      case 1:
        return "bg-blue-600";
      case 2:
        return "bg-yellow-600";
      default:
        return "bg-gray-400";
    }
  }

  function isUsTicker(ticker: string): boolean {
    return ticker.length <= 5 && !ticker.includes(":") && !ticker.includes(".");
  }

  return (
    <div>
      {props.news.map((article) => (
        <div key={article.url} id={article.url} className={`flex gap-4 mb-4 ${props.readingArticleUrl === article.url ? "bg-yellow-100" : ""}`}>
          <div
            className={`min-w-4 cursor-pointer ${addMarkClass(article.read)}`}
            onClick={() => {
              props.markArticle(article.url, article.read + 1);
            }}
          ></div>

          <div>
            <p className="text-gray-500 gap-4 flex mb-2">
              <span
                onClick={(e) => {
                  e.preventDefault();
                  props.markArticle(article.url, article.read + 1);
                }}
              >
                {props.unixTimestampToNewYork(article.published_at)[0]}
              </span>
              <a href={article.url} target="_blank" rel="noopener noreferrer">
                ({article.source})
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  props.setActiveArticleUrl(article.url);
                }}
              >
                (text)
              </a>
            </p>
            <div
              onClick={(e) => {
                e.preventDefault();
                props.markArticle(article.url, article.read + 1);
              }}
              className="whitespace-pre-wrap"
            >
              {article.tickers && (
                <mark className="ner-loc mr-1">
                  {article.tickers.split(" ").map((t, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && " "}
                      {isUsTicker(t) ? <span className="font-semibold">{t}</span> : t}
                      {article.ticker_changes?.[t] !== undefined && (
                        <span className="underline"> {article.ticker_changes[t].toFixed(1)}%</span>
                      )}
                    </React.Fragment>
                  ))}
                </mark>
              )}
              <HighlightedText text={article.title} marks={article.title_marks} />
            </div>
          </div>

          <hr />
        </div>
      ))}
    </div>
  );
};

